import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { detectSubscriptions, totalAnnualSubscriptionCost } from '@/lib/subscription-detector';
import { formatAUD } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, DollarSign, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function SubscriptionDetector() {
  const { data } = useAuth();
  const navigate = useNavigate();

  const subs = useMemo(() => detectSubscriptions(data.transactions), [data.transactions]);
  const annualTotal = useMemo(() => totalAnnualSubscriptionCost(subs), [subs]);

  if (subs.length === 0) return null;

  return (
    <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => navigate('/more/subscriptions')}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Detected Subscriptions</h3>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <DollarSign className="h-3 w-3" />
            <span>{formatAUD(annualTotal)}/yr</span>
          </div>
        </div>

        <div className="space-y-2">
          {subs.slice(0, 5).map((sub, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{sub.description}</p>
                <p className="text-[10px] text-muted-foreground">
                  {sub.occurrences} charges · {sub.frequency}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold">{formatAUD(sub.amount)}</p>
                <p className="text-[10px] text-muted-foreground">{formatAUD(sub.estimatedAnnualCost)}/yr</p>
              </div>
            </div>
          ))}
        </div>

        {subs.length > 5 && (
          <p className="mt-2 text-center text-[10px] text-muted-foreground">
            +{subs.length - 5} more subscriptions detected
          </p>
        )}

        <div className="mt-3 flex items-center justify-center gap-1 text-xs text-primary">
          <span>View all subscriptions</span>
          <ChevronRight className="h-3 w-3" />
        </div>
      </CardContent>
    </Card>
  );
}
